import React, { useState } from 'react'
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogTrigger } from '../../../../components/ui/alert-dialog'
import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import { Combobox } from '../../../../components/ui/combobox';
import { Input } from '../../../../components/ui/input';
import { useBoundStore } from '../../../../zustand';
import { Controller, useForm, useWatch } from "react-hook-form";
import { addHours, format } from 'date-fns';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from '../../../../lib/axios'
import { useAuth } from '../../../../context/AuthProvider';
import { Trans, useLingui } from '@lingui/react/macro';
import { getCustomSettings } from '../../../../config/custom-settings';

type Props = {
    trigger?: React.ReactNode;
    date: Date
    onSuccess?: () => void;
}

type FormData = {
    activity: string;
    intervention: string;
    startTime: string;
    endTime: string;
}

const AddActivity = ({trigger, date, onSuccess}:Props) => {
    const { auth } = useAuth()
    const { t } = useLingui()

    const parameters = useBoundStore((state)=>state.parameters)
    const interventions = useBoundStore((state)=>state.interventions)

    const [open, setopen] = useState(false);

    const { control, handleSubmit, formState:{ errors, isValid }, reset:resetForm, trigger:triggerForm } = useForm<FormData>({
        defaultValues: {
          activity: '',
          intervention: '',
          startTime: format(new Date(), 'HH:mm'),
          endTime: format(addHours(new Date(), 1), 'HH:mm'),
        },
        mode: 'onChange'
    });

    const { mutate } = useMutation({
        mutationFn: async (data:FormData) => {
            if(data.activity === 'WORK'){
                // Add to timeregistration
                await axios.post(`timeregistration?dbid=${auth.data?.databases[0].dbid}`, {
                    timeregistrations: [{
                        interventionid: data.intervention,
                        technicianid: auth.data?.databases[0].technicianid,
                        fromdate: format(date, 'yyyyMMdd'),
                        todate: format(date, 'yyyyMMdd'),
                        fromtime: data.startTime,
                        totime: data.endTime,
                        timestamp: format(new Date(), 'yyyyMMddHHmmss'),
                    }]
                })
            } else {
                // Add to triptime
                await axios.post(`triptime?dbid=${auth.data?.databases[0].dbid}`, {
                    triptimes: [{
                        interventionid: data.intervention,
                        technicianid: auth.data?.databases[0].technicianid,
                        date: format(date, 'yyyyMMdd'),
                        starttime: data.startTime,
                        endtime: data.endTime,
                        duration: "0",
                        activity: data.activity,
                        timestamp: format(new Date(), 'yyyyMMddHHmmss'),
                    }]
                })
            }
        },
        onError: (error) => {
            toast.error(t`Er is een fout opgetreden bij het opslaan van de activiteit.`, { id:'add-activity' })
        },
        onSuccess: (data) => {
            onSuccess && onSuccess()
            toast.success(t`De activiteit is opgeslagen.`, { id:'add-activity' })
            resetForm()
            setopen(false);
        }
    })

    const { data:ints } = useQuery({
        queryKey: [`interventions-${date?.getDay()}`],
        queryFn: async () => {
            const { data } = await axios.get(`interventions`, {
                params: {
                    fromdate: format(date, 'yyyyMMdd'),
                    todate: format(date, 'yyyyMMdd'),
                    dbid: auth.data?.databases[0].dbid
                }
            })
            return data
        }
    })

    const startTime = useWatch({ control, name: 'startTime' });
    const endTime = useWatch({ control, name: 'endTime' });
    const activity = useWatch({ control, name: 'activity' });

    const validateTimes = () => {
        if (startTime && endTime) {
          const start = new Date(`1970-01-01T${startTime}:00`);
          const end = new Date(`1970-01-01T${endTime}:00`);
          return start < end || 'Start tijd moet voor eind tijd liggen';
        }
        return true;
    };

    React.useEffect(() => {
        if (startTime && endTime) {
            triggerForm(['startTime', 'endTime']);
        }
    }, [startTime, endTime, triggerForm]);

    const activities = Array.isArray(parameters) && parameters.filter((p)=>p?.m_iTYPE.toString() === '23' && p?.m_sLANGUAGE === 'N')?.map((p)=>({ label:p?.m_sOMS, value:p?.m_sCODE }))
    const interventionOptions = Array.isArray(ints) 
        ? (
            ints
                ?.filter((i)=>{
                    if(activity === 'WORK' || activity === 'MOVE' || activity === 'HOME'){
                        return !getCustomSettings(auth.data?.databases[0].dbid)?.calendar?.new_activity?.filter_status?.includes(i.m_iSTATUS)
                    } else {
                        return i
                    }
                })
                ?.map((i)=>({ label:i.m_oKLANT.m_sNAAM, value:i.m_iINTERVENTIESID.toString() })) 
        )
        : []
    
    const onSubmit = (data:FormData) => {
        mutate(data)
        
        // if activity === 'WORK' > tijdsregistratie
        // else > triptime (activity = param code)

        setopen(false);
        resetForm();
    };
      
  return (
    <AlertDialog open={open} onOpenChange={setopen}>
        {trigger &&
        <AlertDialogTrigger asChild>
            {trigger}
        </AlertDialogTrigger>
        }
        <AlertDialogContent>
            <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
                <div>
                    <Label><Trans>Activiteit</Trans><span className='ml-1 text-red-600'>*</span></Label>
                    <Controller
                        name="activity"
                        control={control}
                        rules={{ required: t`Activiteit is verplicht` }}
                        render={({ field }) => (
                            <Combobox selectedValue={field.value} onValueChange={({value})=>{ field.onChange(value.toString()) }} options={activities || []} placeholder={t`Selecteer een activiteit...`}/>
                        )}
                    />
                    {errors.activity && <p className="text-sm text-red-500">{errors.activity.message}</p>}
                </div>
                {['WORK', 'HOME', 'MOVE'].includes(activity) && 
                <div>
                    <Label><Trans>Interventie</Trans><span className='ml-1 text-red-600'>*</span></Label>
                    <Controller
                        name="intervention"
                        control={control}
                        rules={{
                            validate: (value) => {
                                if(['WORK', 'HOME', 'MOVE'].includes(activity) && !value){
                                    return t`Interventie is verplicht`
                                }
                                return true
                            }
                        }}
                        render={({ field }) => (
                            <Combobox selectedValue={field.value} onValueChange={({value})=>{ field.onChange(value.toString()) }} options={interventionOptions || []} placeholder={t`Selecteer een interventie...`}/>
                        )}
                    />     
                    {errors.intervention && <p className="text-sm text-red-500">{errors.intervention.message}</p>}           
                </div>
                }
                <div>
                    <Label><Trans>Start tijd</Trans><span className='ml-1 text-red-600'>*</span></Label>
                    <Controller
                        name="startTime"
                        control={control}
                        rules={{
                            required: t`Start tijd is verplicht`,
                            validate: validateTimes,
                        }}
                        render={({ field }) => (
                            <Input {...field} type='time'/>
                        )}
                    />
                    {errors.startTime && <p className="text-sm text-red-500">{errors.startTime.message}</p>}      
                </div>
                <div>
                    <Label><Trans>Eind tijd</Trans><span className='ml-1 text-red-600'>*</span></Label>
                    <Controller
                        name="endTime"
                        control={control}
                        rules={{
                            required: t`Eind tijd is verplicht`,
                            validate: validateTimes,
                        }}
                        render={({ field }) => (
                            <Input {...field} type='time'/>
                        )}
                    />
                    {errors.endTime && <p className="text-sm text-red-500">{errors.endTime.message}</p>}      
                </div>
                <AlertDialogFooter>
                    <AlertDialogCancel><Trans>Annuleren</Trans></AlertDialogCancel>
                    <Button type="submit" disabled={!isValid}><Trans>Opslaan</Trans></Button>
                </AlertDialogFooter>
            </form>
        </AlertDialogContent>
    </AlertDialog>
  )
}

export default AddActivity